import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MessageSquare, X } from 'lucide-react';
import { API } from '../../shared/config';
import { useAuth } from '../../contexts/AuthContext';
import { getPhotoUrl } from '../../shared/utils';

const TYPE_LABEL = { PRIVE: 'Privé', GENERAL: 'Général' };
const TYPE_COLOR = { PRIVE: '#3b82f6', GENERAL: '#0d9488' };
const ouvrirChat = () => window.dispatchEvent(new CustomEvent('navigate-to-chat'));

const AvatarMsg = ({ msg }) => {
  if (msg.type === 'GENERAL' || msg.type === 'GROUPE') {
    const g = msg.type === 'GENERAL';
    return <div className={`h-9 w-9 rounded-full flex items-center justify-center text-white text-sm bg-gradient-to-br ${g ? 'from-blue-400 to-blue-600' : 'from-orange-400 to-orange-600'}`}>{g ? 'G' : 'Gr'}</div>;
  }
  const u = msg.expediteur;
  if (u?.photo_url) return <img src={getPhotoUrl(u.photo_url)} alt="" className="h-9 w-9 rounded-full object-cover" />;
  const bg = u?.role === 'Médecin' ? 'from-blue-400 to-blue-600' : u?.role === 'Assistant' ? 'from-emerald-400 to-emerald-600' : 'from-purple-400 to-purple-600';
  return <div className={`h-9 w-9 rounded-full flex items-center justify-center text-white text-sm bg-gradient-to-br ${bg}`}>{u?.prenom?.[0]}{u?.nom?.[0]}</div>;
};

const useNonLus = (user) => {
  const [data, setData] = useState({ total: 0, recent: [] });
  useEffect(() => {
    if (!user) return undefined;
    const charger = () => axios.get(`${API}/messages/unread-counts`).then(r => setData({ total: r.data?.total || 0, recent: r.data?.recent || [] })).catch(() => {});
    charger();
    const t = setInterval(() => { if (document.visibilityState === 'visible') charger(); }, 15000);
    return () => clearInterval(t);
  }, [user]);
  return data;
};

/* Bouton Messages de la barre du haut : badge des non-lus + aperçu déroulant. */
export const ChatNavButton = ({ className = '' }) => {
  const { user } = useAuth();
  const { total, recent } = useNonLus(user);
  const [ouvert, setOuvert] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!ouvert) return undefined;
    const f = (e) => { if (ref.current && !ref.current.contains(e.target)) setOuvert(false); };
    document.addEventListener('mousedown', f);
    return () => document.removeEventListener('mousedown', f);
  }, [ouvert]);
  if (!user) return null;
  const aller = () => { setOuvert(false); ouvrirChat(); };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button type="button" onClick={() => setOuvert(o => !o)} title="Messages"
        className="relative inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[#1173D4]/30 bg-[#EAF2FC]/50 text-sm font-medium text-[#0B1F3A] hover:bg-[#EAF2FC] transition-colors" data-testid="nav-chat-btn">
        <MessageSquare className="h-4 w-4 text-[#1173D4]" /><span className="hidden xl:inline">Messages</span>
        {total > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="nav-chat-badge">{total}</span>}
      </button>
      {ouvert && (
        <div className="absolute left-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden" data-testid="nav-chat-preview">
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-br from-[#1173D4] to-[#0d5cab] text-white">
            <span className="text-sm font-semibold">{total > 0 ? `${total} message${total > 1 ? 's' : ''} non-lu${total > 1 ? 's' : ''}` : 'Messages'}</span>
            <button type="button" onClick={() => setOuvert(false)} data-testid="nav-chat-close"><X className="h-4 w-4" /></button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {recent.length === 0 ? <div className="p-4 text-center text-gray-500 text-sm">Aucun message non-lu</div> : recent.map((m, i) => (
              <button key={i} type="button" onClick={aller} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#EAF2FC]/60 border-b border-gray-100 last:border-0" data-testid={`nav-chat-item-${i}`}>
                <AvatarMsg msg={m} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-gray-800 truncate">{m.label || `${m.expediteur?.prenom} ${m.expediteur?.nom}`}<span className="ml-1.5 text-[10px] font-normal text-gray-500">{TYPE_LABEL[m.type] || 'Groupe'}</span></div>
                  <div className="text-xs text-gray-500 truncate">{m.last_message}</div>
                </div>
                <span className="rounded-full px-2 py-0.5 text-[11px] font-bold text-white" style={{ background: TYPE_COLOR[m.type] || '#f97316' }}>{m.count}</span>
              </button>
            ))}
          </div>
          <button type="button" onClick={aller} className="w-full py-2.5 text-sm font-semibold text-[#1173D4] border-t border-gray-100 hover:bg-gray-50" data-testid="nav-chat-open-all">Ouvrir la messagerie</button>
        </div>
      )}
    </div>
  );
};

export default ChatNavButton;
